import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@shared/core';
import { Close } from '@withjoy/joykit/icons';
import { Flex, TextV2 } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAutoPopulation } from '@shared/utils/accommodations';
import { useUserConsent } from '@shared/utils/userConsent';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

/**
 * Component to display a summary of auto-populated accommodations.
 *
 * Shows counts of newly populated and already existing accommodations.
 * Provides an option to dismiss the summary, which clears the stored counts.
 */
export const AutoPopulatedAccommodationsSummary = React.memo(
  ({ accommodationsCount, autoPopulatedHotelsCount }: { accommodationsCount: number; autoPopulatedHotelsCount: number }) => {
    const { tq } = useTranslation('travelEditor');

    const [showSummary, setShowSummary] = useState(true);

    const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
    const [asyncHotelAutopopulationEnabled] = useIsFeatureEnabled('asyncHotelAutopopulationEnabled');

    const { autoPopulationSummary } = useAutoPopulation({
      mode: 'poll-only',
      context: 'AutoPopulatedAccommodationsSummary',
    });

    const { newlyPopulatedCount, alreadyExistsCount } = autoPopulationSummary.get(accommodationsCount);

    const onAddedHotelsBarClose = useEventCallback(() => {
      setShowSummary(false);
      // Clear the local storage so that the bar doesn't show up again on re-render
      autoPopulationSummary.remove();
      setUserConsent('hasSeenAutoPopulationSummary', true);
    });

    /**
     * Single source of truth to check event has auto populated hotels or not. Mark seen when user sees hotels.
     * newlyPopulatedCount: It gives count of hotels auto populated by async api and existing local storage flag.
     * autoPopulatedHotelsCount: With existing api, sometimes api timeout happens but it auto populate hotels, so used as a fallback does event has auto populated hotels or not.
     * */
    const { finalNewlyPopulatedCount, finalAlreadyExistsCount } = newlyPopulatedCount
      ? { finalNewlyPopulatedCount: newlyPopulatedCount, finalAlreadyExistsCount: alreadyExistsCount }
      : { finalNewlyPopulatedCount: autoPopulatedHotelsCount, finalAlreadyExistsCount: accommodationsCount ? Math.max(0, accommodationsCount - autoPopulatedHotelsCount) : 0 };

    const showMessage = useMemo(() => {
      if (finalNewlyPopulatedCount > 0 && finalAlreadyExistsCount > 0) {
        return tq`We added ${finalNewlyPopulatedCount} new hotels to ${finalAlreadyExistsCount} already on the page.`;
      }
      return tq`We added ${finalNewlyPopulatedCount} new hotels to the page.`;
    }, [tq, finalNewlyPopulatedCount, finalAlreadyExistsCount]);

    useEffect(() => {
      if (loadingUserConsent) return;
      if (accommodationsCount && finalNewlyPopulatedCount > 0) {
        const hasSeenAccommodationsRecommendations = getUserConsent('hasSeenAccommodationsRecommendations');
        if (!hasSeenAccommodationsRecommendations) {
          setUserConsent('hasSeenAccommodationsRecommendations', true);
        }
      }
    }, [getUserConsent, setUserConsent, loadingUserConsent, accommodationsCount, finalNewlyPopulatedCount]);

    const hasSeenAutoPopulationSummary = getUserConsent('hasSeenAutoPopulationSummary');

    if (!finalNewlyPopulatedCount || !showSummary || !accommodationsCount || asyncHotelAutopopulationEnabled || hasSeenAutoPopulationSummary || loadingUserConsent) return null;

    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingX={4}
        paddingY={3}
        marginTop={3}
        borderRadius={12}
        marginX={6}
        border="1px solid"
        color="newUIBrandPrimary"
        borderColor="newUIBrandPrimary"
        backgroundColor="rgba(73, 81, 239, 0.10)"
        role="status"
        aria-live="polite"
      >
        <TextV2 typographyVariant="label2">{showMessage}</TextV2>
        <Close
          size={16}
          cursor="pointer"
          aria-label={tq`Dismiss summary`}
          tabIndex={0}
          onClick={onAddedHotelsBarClose}
          role="button"
        />
      </Flex>
    );
  },
);
